"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShopBookClient = void 0;
const ClientProxy_1 = require("./ClientProxy");
/**
 * Creates an instance of ShopBook Client
 *
 * This is used to do all the request relative to Shopbook API.
 *
 * @public
 */
class ShopBookClient {
    /**
     * Creates an instance of ShopBook Client.
     *
     * The constructor will create an instance of the ProxyClient classes:
     * - The clientProxy instance is created with the apiBaseURLProxy, domainKey, and headerRefererOverride parameters.
     *
     * Since the API call of ShopBook have been migrated to the Proxy, in true, this is the clientProxy who do the call.
     * But for use clarity, the call have been split into different classes.
     *
     * @param apiBaseURLProxy - The base URL of the proxy.
     * @param domainKey - The domain key for the proxy.
     * @param headerRefererOverride - This is a OPTIONAL parameter: header referer override for the proxy. (use case: when you want to change the referer for local developpement)
     *
     * @remarks
     * The parameters `headerRefererOverride` must only be use for local developpement.
     */
    constructor(apiBaseURLProxy, domainKey, headerRefererOverride) {
        this.apiBaseURLProxy = apiBaseURLProxy;
        this.domainKey = domainKey;
        this.headerRefererOverride = headerRefererOverride;
        this.clientProxy = new ClientProxy_1.ProxyClient(this.apiBaseURLProxy, this.domainKey, this.headerRefererOverride);
    }
    /**
     * Make an API call to search for areas
     *
     * @param body - structure RequestAreaSearch contain information for the request
     *
     * @remarks
     * When endDate is not define, apply a default value: startDate
     *
     * @remarks startDate & endDate are both "Date" Type (not string)
     *
     * @internal
     */
    apiCallAreaSearch(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Format the body of the request
            // startDate and endDate must have a specific format: YYYY-MM-DDZ
            // When endDate is not define, apply a default value: startDate
            // we use a tips to format date with yyyy-mm-dd without use lib or handle all case with locale where default date is yyyy-mm-dd
            // https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd
            const reqBody = Object.assign(Object.assign({}, body), { startDate: body.startDate.toLocaleDateString("en-CA") + "Z", endDate: !body.endDate || body.endDate.toString() === "Invalid Date"
                    ? new Date(new Date().setDate(body.startDate.getDate()))
                        .toISOString()
                        .slice(0, 10) + "Z"
                    : body.endDate.toLocaleDateString("en-CA") + "Z" });
            return this.clientProxy.apiShopBookAreaSearch(reqBody);
        });
    }
    /**
     * Make an API call to package details
     *
     * @param body - structure APIRequestPackageDetails contain information for the request
     *
     * @internal
     */
    apiCallPackageDetails(body) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.clientProxy.apiShopBookPackageDetails(body);
        });
    }
    /**
     * Make an API call to get a list of locations
     *
     * @param body - structure RequestAreaSearch contain information for the request
     *
     * @returns an object of type ResponseLocations which contains an array of location objects, each with id, name, startingAtPrice and currencyCode.
     *
     * @remarks
     * When endDate is not define, apply a default value: startDate
     *
     * @remarks startDate & endDate are both "Date" Type (not string)
     *
     * @remarks This fields are required:
     * - startDate
     * - numberOfAttendees
     * - locations[].id
     *
     * @throws `Error` class contain information about the failed request
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ShopBookClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getLocations({
     *  "startDate": new Date(),
     *  "endDate": new Date(), // maybe empty or null
     *  "numberOfAttendees": 1,
     *  "locations": [
     *      {
     *          "id": "ChuckNorrisID",
     *      }
     *  ]
     * }).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getLocations(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use the call for Area Search API
            const apiResponse = yield this.apiCallAreaSearch(body);
            // Format the response
            const callResponse = {
                locations: apiResponse.data.locations.map((e) => ({
                    id: e.id,
                    name: e.name,
                    startingAtPrice: e.startingAtPrice,
                    currencyCode: e.packages[0].currencyCode
                }))
            };
            return callResponse;
        });
    }
    /**
     * Make an API call to get a list of packages
     *
     * @param body - structure RequestAreaSearch contain information for the request
     *
     * @returns an object of type ResponsePackages which contains an array of package objects, each with id, name, eventPriceTotal, description and currencyCode.
     *
     * @throws `Error` class contain information about the failed request
     *
     * @remarks
     * When endDate is not define, apply a default value: startDate
     *
     * @remarks startDate & endDate are both "Date" Type (not string)
     *
     * @remarks This fields are required:
     * - startDate
     * - numberOfAttendees
     * - locations[].id
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ShopBookClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getPackages({
     *  "startDate": new Date(),
     *  "endDate": new Date(), // maybe empty or null
     *  "numberOfAttendees": 1,
     *  "locations": [
     *      {
     *          "id": "ChuckNorrisID",
     *      }
     *  ]
     * }).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getPackages(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use the call for Area Search API
            const apiResponse = yield this.apiCallAreaSearch(body);
            // Format the response
            const callResponse = {
                locations: apiResponse.data.locations.map((location) => ({
                    packages: location.packages.map((pkg) => ({
                        xTaxes: pkg.xTaxes,
                        name: pkg.name,
                        description: pkg.description,
                        eventPriceTotal: pkg.eventPriceTotal,
                        id: pkg.id,
                        currencyCode: pkg.currencyCode
                    }))
                }))
            };
            return callResponse;
        });
    }
    /**
     * Make an API call to get a list of function Rooms
     *
     * @param body - structure RequestAreaSearch contain information for the request
     *
     * @returns an object of type ResponseFuncRooms which contains an array of locations objects
     *
     * @throws `Error` class contain information about the failed request
     *
     * @remarks
     * When endDate is not define, apply a default value: startDate
     *
     * @remarks startDate & endDate are both "Date" Type (not string)
     *
     * @remarks startDate & endDate are both "Date" Type (not string)
     *
     * @remarks This fields are required:
     * - startDate
     * - numberOfAttendees
     * - locations[].id
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ShopBookClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getFunctionRooms({
     *  "startDate": new Date(),
     *  "endDate": new Date(), // maybe empty or null
     *  "numberOfAttendees": 1,
     *  "locations": [
     *      {
     *          "id": "ChuckNorrisID",
     *      }
     *  ]
     * }).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getFunctionRooms(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use the call for Area Search API
            const apiResponse = yield this.apiCallAreaSearch(body);
            // Format the response
            const callResponse = {
                locations: apiResponse.data.locations.map((location) => ({
                    packages: location.packages.map((pkg) => ({
                        xTaxes: pkg.xTaxes,
                        isInclusive: pkg.isInclusive,
                        id: pkg.id,
                        name: pkg.name,
                        description: pkg.description,
                        eventPriceTotal: pkg.eventPriceTotal,
                        currencyCode: pkg.currencyCode,
                        functionRoomSetups: pkg.functionRoomSetups.map((functionRoomSetup) => ({
                            name: functionRoomSetup.name,
                            functionRooms: functionRoomSetup.timeSlots.map((timeSlot) => ({
                                timeSlots: timeSlot.days.map((day) => ({
                                    days: day.functionRooms.map((functionRoom) => ({
                                        id: functionRoom.id,
                                        name: functionRoom.name,
                                        capacity: functionRoom.capacity,
                                        events: functionRoom.events.map((event) => ({
                                            isInclusive: event.isInclusive,
                                            rental: event.rental,
                                            id: event.id,
                                            name: event.name,
                                            startTime: event.startTime,
                                            endTime: event.endTime,
                                            eventClassificationId: event.eventClassificationId,
                                            rentalCalculation: event.rentalCalculation
                                        }))
                                    }))
                                }))
                            }))
                        }))
                    }))
                }))
            };
            return callResponse;
        });
    }
    /**
     * Make an API call to get package details
     *
     * @param body - structure RequestPackageDetail contain information for the request
     *
     * @returns an object of type ResponsePackageDetails
     *
     * @throws `Error` class contain information about the failed request
     *
     * @remarks This fields are required:
     * - id
     * - numberOfAttendees
     * - locations.id
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ShopBookClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getPackageDetails({
     *  "id": "idPackage",
     *  "numberOfAttendees": 1,
     *  "location": {
     *      "id": "ChuckNorrisID",
     *  }
     * }).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     * ```
     */
    getPackageDetails(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use the call for Package Details API
            const apiResponse = yield this.apiCallPackageDetails(body);
            // Format the response
            const callResponse = {
                packageDetails: apiResponse.data.packageDetails
            };
            return callResponse;
        });
    }
    /**
     * Make an API call to get reservation
     *
     * @param body - structure RequestPackageDetail contain information for the request
     *
     * @returns an object of type ResponseReservation
     *
     * @throws `Error` class contain information about the failed request
     *
     * @remarks This fields are required:
     * - id
     * - numberOfAttendees
     * - locations.id
     *
     * @example How to Use
     *
     * ```typescript
     * let client = new ShopBookClient(BASE_URL_PROXY, DOMAIN_KEY)
     * client.getReservation({
     *  "id": "idPackage",
     *  "numberOfAttendees": 1,
     *  "location": {
     *      "id": "ChuckNorrisID",
     *  }
     * }).then((response) => {
     *     console.log(response)
     * }).catch((error) => {
     *     console.log(error)
     * })
     *
     * ```
     */
    getReservation(body) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use the call for Package Details API
            const apiResponse = yield this.apiCallPackageDetails(body);
            const data = apiResponse.data;
            // Format the response
            const callResponse = {
                locations: [
                    {
                        package: {
                            name: data.packageDetails.name
                        }
                    }
                ],
                taxes: data.packageDetails.pricing.totalTax +
                    data.packageDetails.functionRooms[0].pricing.totalTax +
                    data.packageDetails.packageEvents[0].addOnItems.reduce((acc, element) => acc + element.pricing.totalTax, 0),
                gratuityFee: data.packageDetails.pricing.totalGratuity +
                    data.packageDetails.functionRooms[0].pricing.totalGratuity +
                    data.packageDetails.packageEvents[0].addOnItems.reduce((acc, element) => acc + element.pricing.totalGratuity, 0),
                adminFee: data.packageDetails.pricing.totalAdmin +
                    data.packageDetails.functionRooms[0].pricing.totalAdmin +
                    data.packageDetails.packageEvents[0].addOnItems.reduce((acc, element) => acc + element.pricing.totalAdmin, 0),
                totalReservation: data.packageDetails.pricing.grandTotal +
                    data.packageDetails.functionRooms[0].pricing.grandTotal +
                    data.packageDetails.packageEvents[0].addOnItems.reduce((acc, element) => acc + element.pricing.grandTotal, 0)
            };
            return callResponse;
        });
    }
}
exports.ShopBookClient = ShopBookClient;
//# sourceMappingURL=ClientShopBook.js.map